import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type DragEvent } from "react";
import {
  COMMENT_FILE_ACCEPT,
  MAX_TASK_ATTACHMENT_BYTES,
  type TaskAttachment,
  type WriteRejection,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";

const ACCEPTED_TYPES = COMMENT_FILE_ACCEPT.split(",");

/** A pasted screenshot arrives called "image.png"; give it a name worth keeping. */
function screenshotName(file: File): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  const stamp = `${String(now.getFullYear())}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const extension = file.type.split("/")[1] ?? "png";
  return `screenshot-${stamp}.${extension}`;
}

/** The task's files and the actions on them. */
export type TaskAttachmentsState = ReturnType<typeof useTaskAttachments>;

/**
 * A task's files (FR-TSK-13, #625) and the three ways in: the Attach button, a
 * file dropped anywhere on the task, and a screenshot pasted while it is open.
 *
 * The same shape as Docs' `useAttachments`: a refused file is shown with its
 * reason and nothing is stored, and the size is checked here too so a file over
 * the cap never makes the round trip just to be refused. The server decides the
 * type from the bytes; the check here only saves the trip.
 */
export function useTaskAttachments(taskId: string, canEdit: boolean) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(0);

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ["tasks"] });
  }

  const uploadMutation = useMutation({
    mutationFn: async (file: File) =>
      data.tasks.uploadAttachment(taskId, {
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        data: new Uint8Array(await file.arrayBuffer()),
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (attachmentId: string) =>
      data.tasks.deleteAttachment(taskId, attachmentId),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        refresh();
      } else {
        setRejection(result.rejection);
      }
    },
  });

  async function upload(files: File[]) {
    setRejection(null);
    for (const file of files) {
      if (file.size > MAX_TASK_ATTACHMENT_BYTES) {
        setRejection({
          kind: "invalid",
          message: `"${file.name}" is over the 25 MB limit.`,
          fields: { file: "Too large" },
        });
        continue;
      }
      if (file.type !== "" && !ACCEPTED_TYPES.includes(file.type)) {
        setRejection({
          kind: "invalid",
          message: `"${file.name}" is not an image or a PDF. Only images (PNG, JPEG, GIF, WebP) and PDFs are accepted.`,
          fields: { file: "Not an accepted type" },
        });
        continue;
      }
      setUploading((count) => count + 1);
      try {
        const result = await uploadMutation.mutateAsync(file);
        if (result.ok) refresh();
        else setRejection(result.rejection);
      } catch {
        setRejection({
          kind: "blocked",
          message: "The upload did not go through. Try again.",
        });
      } finally {
        setUploading((count) => count - 1);
      }
    }
  }

  function handleFiles(files: FileList | File[] | null) {
    if (!canEdit || !files || files.length === 0) return;
    void upload(Array.from(files));
  }

  // A screenshot pasted while the task is open (Ctrl+V) becomes an attachment.
  // Text is left alone: it must keep pasting into the comment box.
  const handleFilesRef = useRef(handleFiles);
  useEffect(() => {
    handleFilesRef.current = handleFiles;
  });
  useEffect(() => {
    if (!canEdit) return;
    function onPaste(event: ClipboardEvent) {
      const clipboard = event.clipboardData;
      if (!clipboard || clipboard.files.length === 0) return;
      if (clipboard.types.includes("text/plain")) return;
      const files = Array.from(clipboard.files).map((file) =>
        file.type.startsWith("image/") &&
        (file.name === "" || /^image\.\w+$/i.test(file.name))
          ? new File([file], screenshotName(file), { type: file.type })
          : file,
      );
      event.preventDefault();
      handleFilesRef.current(files);
    }
    document.addEventListener("paste", onPaste);
    return () => {
      document.removeEventListener("paste", onPaste);
    };
  }, [canEdit]);

  function requestDelete(attachment: TaskAttachment) {
    if (
      window.confirm(`Delete "${attachment.filename}"? This cannot be undone.`)
    ) {
      deleteMutation.mutate(attachment.id);
    }
  }

  /** Drag-and-drop onto whatever element spreads these (the whole task). */
  function dropProps() {
    if (!canEdit) return {};
    return {
      onDragOver: (event: DragEvent) => {
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault();
        setDragOver(true);
      },
      onDragLeave: (event: DragEvent) => {
        // Leaving for a child of the task is not leaving the task.
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
          return;
        }
        setDragOver(false);
      },
      onDrop: (event: DragEvent) => {
        if (event.dataTransfer.files.length === 0) return;
        event.preventDefault();
        setDragOver(false);
        handleFiles(event.dataTransfer.files);
      },
    };
  }

  /**
   * The hidden picker the Attach button opens. No `capture`: on a phone the
   * browser offers the camera, the photo library and files in one sheet.
   */
  const picker = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      accept={COMMENT_FILE_ACCEPT}
      className="hidden"
      data-testid="task-attachment-input"
      onChange={(event) => {
        handleFiles(event.target.files);
        event.target.value = "";
      }}
    />
  );

  return {
    uploading: uploading > 0,
    deleting: deleteMutation.isPending,
    rejection,
    dragOver,
    picker,
    canEdit,
    openPicker: () => fileInputRef.current?.click(),
    dropProps,
    requestDelete,
  };
}
